import { useCallback, useEffect, useRef, useState } from 'react';
import { publicTweaksSchema } from '@/shared/marketing-schemas';
import { TWEAK_DEFAULTS, type TweaksState } from '@/shared/tweaks-defaults';

// Public render path: hydrates from /api/marketing/public-tweaks, falls back
// to TWEAK_DEFAULTS if the endpoint errors so the page always paints something.
export function usePublicTweaks(): { tweaks: TweaksState; isLoading: boolean } {
  const [tweaks, setTweaks] = useState<TweaksState>(TWEAK_DEFAULTS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/marketing/public-tweaks')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((raw) => publicTweaksSchema.parse(raw))
      .then((data) => {
        if (!cancelled) {
          setTweaks(data);
          setIsLoading(false);
        }
      })
      .catch(() => {
        // Public route doesn't fail loudly — defaults already populated.
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { tweaks, isLoading };
}

type SetTweak = <K extends keyof TweaksState>(key: K, value: TweaksState[K]) => void;

// Admin path: hydrates from /api/admin/tweaks (requires admin auth on the
// Worker side). Each setTweak() optimistically updates local state and
// debounces a PUT 600ms; on failure, reverts to last server-confirmed state.
export function useAdminTweaks(): {
  tweaks: TweaksState;
  setTweak: SetTweak;
  isSaving: boolean;
  lastSavedAt: number | null;
  error: Error | null;
} {
  const [tweaks, setTweaks] = useState<TweaksState>(TWEAK_DEFAULTS);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastServerState = useRef<TweaksState>(TWEAK_DEFAULTS);

  useEffect(() => {
    void fetch('/api/admin/tweaks', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Not admin'))))
      .then((raw) => publicTweaksSchema.parse(raw))
      .then((data) => {
        setTweaks(data);
        lastServerState.current = data;
      })
      .catch(() => {
        // Not admin or fetch failed — caller will hide the panel based on role.
      });
  }, []);

  const setTweak: SetTweak = useCallback((key, value) => {
    setTweaks((current) => {
      const next = { ...current, [key]: value } as TweaksState;
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setIsSaving(true);
        void fetch('/api/admin/tweaks', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(next),
        })
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            lastServerState.current = next;
            setLastSavedAt(Date.now());
            setError(null);
          })
          .catch((err: Error) => {
            setError(err);
            setTweaks(lastServerState.current);
          })
          .finally(() => setIsSaving(false));
      }, 600);
      return next;
    });
  }, []);

  return { tweaks, setTweak, isSaving, lastSavedAt, error };
}
